import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { createBillingPortalSession } from '../../services/api';

const menuBtn = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '12px 14px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: 14,
};

/** Account icon (top right): sign in / exam list / resume practice / sign out. */
export default function UserMenuControl({ onOpenLogin, onNavigate, canResumePractice }) {
  const { t } = useTranslation();
  const { user, isGuest, isJagE, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  // Le compte « guest » est partagé : il n'y a pas de session personnelle à
  // déconnecter. Le menu doit donc proposer **Sign in** et masquer Sign out
  // (sinon « se déconnecter » revient à perdre l'auto-login guest et boucler).
  const isPublicVisitor = !user || isGuest;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const run = (fn) => {
    setOpen(false);
    fn();
  };

  const label = isPublicVisitor
    ? t('userMenu.account')
    : String(user.username || user.email || user.id || '').trim() || t('userMenu.account');

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: '2px solid rgba(255,255,255,0.85)',
          backgroundColor: isPublicVisitor ? 'rgba(255,255,255,0.12)' : 'rgba(52,152,219,0.35)',
          color: '#fff',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            minWidth: 220,
            backgroundColor: '#fff',
            color: '#2c3e50',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            zIndex: 10002,
          }}
        >
          {!isPublicVisitor && (
            <div
              style={{
                padding: '10px 14px',
                fontSize: 13,
                borderBottom: '1px solid #ecf0f1',
                maxWidth: 280,
                wordBreak: 'break-word',
              }}
            >
              {label}
            </div>
          )}

          {onNavigate && (
            <button
              type="button"
              role="menuitem"
              style={menuBtn}
              onClick={() => run(() => onNavigate('catalog'))}
            >
              {t('userMenu.examList')}
            </button>
          )}

          {!isPublicVisitor && canResumePractice && onNavigate && (
            <button
              type="button"
              role="menuitem"
              style={menuBtn}
              onClick={() => run(() => onNavigate('practice'))}
            >
              {t('userMenu.resumePractice')}
            </button>
          )}

          {!isPublicVisitor && isJagE && onNavigate && (
            <button
              type="button"
              role="menuitem"
              style={menuBtn}
              onClick={() => run(() => onNavigate('progress_dashboard'))}
            >
              {t('userMenu.progressDashboard')}
            </button>
          )}

          {!isPublicVisitor && isJagE && onNavigate && (
            <button
              type="button"
              role="menuitem"
              style={menuBtn}
              onClick={() => run(() => onNavigate('payment_dashboard'))}
            >
              {t('userMenu.paymentDashboard')}
            </button>
          )}

          {!isPublicVisitor && (
            <button
              type="button"
              role="menuitem"
              style={menuBtn}
              onClick={() =>
                run(async () => {
                  try {
                    const data = await createBillingPortalSession();
                    if (data?.url) {
                      window.location.href = data.url;
                      return;
                    }
                    window.alert(data?.message || t('billing.portal_failed'));
                  } catch (err) {
                    const msg =
                      err?.response?.data?.message || err.message || t('billing.portal_failed');
                    window.alert(msg);
                  }
                })
              }
            >
              {t('userMenu.invoices')}
            </button>
          )}

          {!isPublicVisitor && <div style={{ height: 1, backgroundColor: '#ecf0f1' }} />}

          {isPublicVisitor ? (
            <button
              type="button"
              role="menuitem"
              onClick={() => run(() => onOpenLogin && onOpenLogin())}
              style={menuBtn}
            >
              {t('userMenu.signIn')}
            </button>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => run(() => logout())}
              style={menuBtn}
            >
              {t('userMenu.signOut')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
